import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Globe, ArrowLeft, Play, Send, Lock, CheckCircle2,
  Lightbulb, FileText, Calculator, Award, ChevronRight,
  Loader2, MessageCircle, User, Bot
} from 'lucide-react';

function ProposalBuilder() {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth.token;
  
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('idea');
  const [chatMessages, setChatMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [sending, setSending] = useState(false);
  const [ideaLocked, setIdeaLocked] = useState(false);

  const sections = [
    { id: 'idea', title: 'Idea Discovery', icon: Lightbulb, description: 'Define and refine your business idea' },
    { id: 'technical', title: 'Technical Proposal', icon: FileText, description: 'Build your business plan' },
    { id: 'financial', title: 'Financial Forecast', icon: Calculator, description: 'Create financial projections' },
    { id: 'endorsement', title: 'Endorsement Review', icon: Award, description: 'Score against criteria' },
  ];

  useEffect(() => {
    if (!token || !proposalId) return;
    
    const fetchProposal = async () => {
      try {
        const res = await axios.get(API + '/ai/proposals/' + proposalId, {
          headers: { Authorization: 'Bearer ' + token }
        });
        setProposal(res.data);
        setIdeaLocked(res.data.status === 'idea_locked' || res.data.status === 'complete');
        
        // Load existing chat messages for idea section
        if (res.data.idea_chat) {
          setChatMessages(res.data.idea_chat);
        } else {
          // Initial AI message
          setChatMessages([{
            role: 'assistant',
            content: "Welcome! I'm your AI business advisor. Let's discover and refine your business idea together.\n\nTell me about your business concept. What problem are you solving, and who is your target audience?"
          }]);
        }
      } catch (err) {
        toast.error('Failed to load proposal');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProposal();
  }, [token, proposalId, navigate]);

  async function handleSendMessage() {
    if (!userInput.trim() || sending) return;
    
    const newMessage = { role: 'user', content: userInput };
    setChatMessages(prev => [...prev, newMessage]);
    setUserInput('');
    setSending(true);

    try {
      const res = await axios.post(API + '/ai/proposals/' + proposalId + '/chat', {
        section: activeSection,
        message: userInput
      }, {
        headers: { Authorization: 'Bearer ' + token }
      });
      
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
      
      // Check if idea should be locked
      if (res.data.idea_locked) {
        setIdeaLocked(true);
        setProposal(prev => ({
          ...prev,
          idea_title: res.data.idea_title,
          category: res.data.category,
          status: 'idea_locked'
        }));
        toast.success('Your business idea has been locked! You can now proceed to the next sections.');
      }
    } catch (err) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  }

  function handleLockIdea() {
    // Trigger idea lock
    setSending(true);
    axios.post(API + '/ai/proposals/' + proposalId + '/lock-idea', {}, {
      headers: { Authorization: 'Bearer ' + token }
    })
    .then(res => {
      setIdeaLocked(true);
      setProposal(prev => ({
        ...prev,
        idea_title: res.data.idea_title,
        category: res.data.category,
        status: 'idea_locked'
      }));
      toast.success('Your business idea has been locked!');
    })
    .catch(() => {
      toast.error('Failed to lock idea. Please continue the conversation.');
    })
    .finally(() => {
      setSending(false);
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="proposal-builder">
      {/* Top Navigation */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="font-bold text-slate-900">
                  {proposal?.idea_title || 'New Proposal'}
                </h1>
                <p className="text-sm text-slate-500">
                  {proposal?.category || 'Define your business idea'}
                </p>
              </div>
            </div>
            
            <Badge className={ideaLocked ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
              {ideaLocked ? (
                <><CheckCircle2 className="w-3 h-3 mr-1" /> Idea Locked</>
              ) : (
                <><Lightbulb className="w-3 h-3 mr-1" /> Discovering Idea</>
              )}
            </Badge>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          {/* Sidebar - Section Navigation */}
          <div className="w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h2 className="font-semibold text-slate-900">Proposal Sections</h2>
              </div>
              
              {sections.map((section, index) => {
                const isActive = activeSection === section.id;
                const isLocked = !ideaLocked && section.id !== 'idea';
                const IconComponent = section.icon;
                
                return (
                  <button
                    key={section.id}
                    onClick={() => !isLocked && setActiveSection(section.id)}
                    disabled={isLocked}
                    className={'w-full p-4 flex items-center gap-3 text-left transition-colors border-b border-slate-50 last:border-0 ' +
                      (isActive ? 'bg-red-50' : 'hover:bg-slate-50') +
                      (isLocked ? ' opacity-50 cursor-not-allowed' : '')}
                  >
                    <div className={'w-10 h-10 rounded-xl flex items-center justify-center ' +
                      (isActive ? 'bg-red-500' : isLocked ? 'bg-slate-100' : 'bg-slate-100')}>
                      {isLocked ? (
                        <Lock className="w-5 h-5 text-slate-400" />
                      ) : (
                        <IconComponent className={'w-5 h-5 ' + (isActive ? 'text-white' : 'text-slate-600')} />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={'font-semibold ' + (isActive ? 'text-red-700' : 'text-slate-900')}>
                        {section.title}
                      </p>
                      <p className="text-xs text-slate-500">{section.description}</p>
                    </div>
                    {!isLocked && <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </button>
                );
              })}
            </div>

            {/* Video Tutorial Card */}
            <div className="mt-6 bg-slate-900 rounded-2xl overflow-hidden">
              <div className="aspect-video relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <button className="w-14 h-14 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600 transition-colors">
                    <Play className="w-6 h-6 text-white ml-1" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                <p className="text-white font-semibold text-sm">Watch Tutorial</p>
                <p className="text-slate-400 text-xs">Learn how to complete this section</p>
              </div>
            </div>
          </div>

          {/* Main Content - Chat Interface */}
          <div className="flex-1 flex flex-col bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" style={{ height: 'calc(100vh - 180px)' }}>
            {/* Chat Header */}
            <div className="p-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">AI Business Advisor</p>
                  <p className="text-xs text-slate-500">
                    {activeSection === 'idea' ? 'Helping you refine your business idea' : 'Building your proposal'}
                  </p>
                </div>
              </div>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {chatMessages.map((msg, idx) => (
                <ChatMessage key={idx} message={msg} />
              ))}
              
              {sending && (
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-slate-100 rounded-2xl rounded-tl-none px-4 py-3">
                    <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
                  </div>
                </div>
              )}
            </div>

            {/* Lock Idea Button (shown after enough conversation) */}
            {!ideaLocked && activeSection === 'idea' && chatMessages.length >= 6 && (
              <div className="px-6 py-3 bg-amber-50 border-t border-amber-200">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-amber-800">
                    Ready to lock your business idea and proceed?
                  </p>
                  <Button 
                    onClick={handleLockIdea}
                    disabled={sending}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Lock Idea & Continue
                  </Button>
                </div>
              </div>
            )}

            {/* Chat Input */}
            <div className="p-4 border-t border-slate-200">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder={ideaLocked && activeSection === 'idea' 
                    ? "Idea locked. Select another section to continue." 
                    : "Type your message..."}
                  disabled={sending || (ideaLocked && activeSection === 'idea')}
                  className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-slate-50 disabled:cursor-not-allowed"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!userInput.trim() || sending || (ideaLocked && activeSection === 'idea')}
                  className="btn-primary px-6"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Chat Message Component
function ChatMessage({ message }) {
  const isUser = message.role === 'user';
  
  return (
    <div className={'flex items-start gap-3 ' + (isUser ? 'flex-row-reverse' : '')}>
      <div className={'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ' +
        (isUser ? 'bg-slate-200' : 'bg-red-500')}>
        {isUser ? (
          <User className="w-4 h-4 text-slate-600" />
        ) : (
          <Bot className="w-4 h-4 text-white" />
        )}
      </div>
      <div className={'max-w-[70%] rounded-2xl px-4 py-3 ' +
        (isUser ? 'bg-red-500 text-white rounded-tr-none' : 'bg-slate-100 text-slate-800 rounded-tl-none')}>
        <p className="whitespace-pre-wrap">{message.content}</p>
      </div>
    </div>
  );
}

export default ProposalBuilder;
